import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { TrendingUp, DollarSign, Scale, Sprout } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function YieldAnalytics({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/analytics/yield`, { withCredentials: true });
        setData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Yield Analytics</h1>
          <p className="text-base text-[#5C665C] mt-2">Track your garden&apos;s productivity and value</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-[#1A4331]/10 flex items-center justify-center mb-2"><Scale className="w-5 h-5 text-[#1A4331]" /></div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{data?.total_weight_kg || 0}kg</p>
            <p className="text-sm text-[#5C665C] mt-1">Total Harvested</p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-2"><DollarSign className="w-5 h-5 text-[#D4AF37]" /></div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>${data?.estimated_value_nzd || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Estimated Value</p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-[#E2725B]/10 flex items-center justify-center mb-2"><TrendingUp className="w-5 h-5 text-[#E2725B]" /></div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{data?.total_harvests || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Harvest Events</p>
          </div>
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <div className="w-10 h-10 rounded-full bg-[#B4D330]/10 flex items-center justify-center mb-2"><Sprout className="w-5 h-5 text-[#B4D330]" /></div>
            <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{data?.total_plantings || 0}</p>
            <p className="text-sm text-[#5C665C] mt-1">Plantings</p>
          </div>
        </div>

        {data?.by_crop && data.by_crop.length > 0 ? (
          <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
            <h2 className="text-2xl font-semibold text-[#1A4331] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>By Crop</h2>
            <div className="space-y-3">
              {data.by_crop.sort((a, b) => b.total_weight - a.total_weight).map(crop => (
                <div key={crop.crop_name} className="flex items-center justify-between p-4 bg-[#F0F2EB] rounded-2xl">
                  <div className="flex-1">
                    <p className="font-semibold text-[#1A4331]">{crop.crop_name}</p>
                    <p className="text-sm text-[#5C665C]">{crop.count} harvest{crop.count > 1 ? 's' : ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#1A4331]">{crop.total_weight?.toFixed(1) || 0}kg</p>
                    {crop.avg_rating > 0 && <p className="text-xs text-[#D4AF37]">{'★'.repeat(Math.round(crop.avg_rating))}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center">
            <Scale className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#2D241E] mb-2">No harvest data yet</h3>
            <p className="text-[#5C665C]">Record your first harvest from My Crops to see analytics</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default YieldAnalytics;
